import { observer } from 'mobx-react';
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { FormCell, Grid, GridItem, NavigationItem } from 'ui-toolkit';
import { IPolicyOrderDetails } from '../classes/PolicyOrderDetails';
import { PolicyTypeEnum } from '../classes/PolicyTypeEnum';
import { eSingleFields } from '../context/state/eSingleFields';
import { ModalTriggerButton } from '../context/components/ModalTriggerButton';
import PriceText from './fields/PriceText';
import PropertyAddress from './fields/PropertyAddress';
import OpenLinkInNewTab from './LinkInNewTab';
import PageWithOutsideLinks from './PageWithOutsideLinks';
import PremiumPrice from './PremiumPrice';
import { EditSingleFieldController } from '../../ui-toolkit/orders/components/EditSingleField/EditSingleFieldController';
import { eEditSingleFieldInputType } from '../../ui-toolkit/orders/components/EditSingleField/eEditSingleFieldInputType';
import * as yup from 'yup';
import { useEditTitlePremiumMutation } from 'API/title/TitlePremiumApi';
import { checkForMutationSuccess } from '../../API/checkForMutationSuccess';
import { Show } from '../../components/shared/Show';
import { eTransactionCategory } from '../../ui-toolkit/enums/eTransactionType';
import { IUpdateTitlePremiumRequest } from '../../types/title/IUpdateTitlePremiumRequest';

interface IOrderDetailsProps {
  data: IPolicyOrderDetails;
  editOrderPage: string;
  editAddressUrl: string;
  editAmtOfInsuredFieldName: eSingleFields;
  editBorrowerPage: string;
  cdBuilderPage: string;
  errorMessage: boolean;
  showSaveSuccess: boolean;
  showSaveSpinner: boolean;
  onUpdatePageClick: () => void;
  showMortgageeClause: boolean;
  policyType: PolicyTypeEnum;
  orderId: string;
}

const OrderDetails: React.FC<IOrderDetailsProps> = ({
  data,
  editOrderPage,
  editAddressUrl,
  editAmtOfInsuredFieldName,
  editBorrowerPage,
  cdBuilderPage,
  errorMessage,
  showSaveSuccess,
  showSaveSpinner,
  onUpdatePageClick,
  showMortgageeClause,
  policyType,
  orderId,
}) => {
  const [onOutsideClick, setOnOutsideClick] = useState<boolean>(false);
  const [showPremiumModal, setShowPremiumModal] = useState<boolean>(false);
  const prevSpinner = useRef<boolean>(showSaveSpinner);
  const [editTitlePremium] = useEditTitlePremiumMutation();

  useEffect(() => {
    if (prevSpinner.current && !showSaveSpinner && showSaveSuccess && showPremiumModal) {
      setOnOutsideClick((prevState) => !prevState);
    }
    prevSpinner.current = showSaveSpinner;
  }, [showSaveSpinner, showSaveSuccess, showPremiumModal]);

  const onOpenNewTabClick = () => {
    setOnOutsideClick((prevState) => !prevState);
  };

  const onUpdatePageClickHandler = () => {
    onUpdatePageClick();
  };

  const setPremiumModal = (value: boolean) => setShowPremiumModal(value);

  const premiumValue = useMemo(() => {
    return data.premiums && data.premiums.length === 1 ? (data.premiums[0].total ?? null) : null;
  }, [data.premiums]);

  const submitPremiumUpdate = useCallback(async (value: number | null): Promise<{ message: string }> => {
    const body: IUpdateTitlePremiumRequest =
      policyType === PolicyTypeEnum.owner ? { ownerAmount: value } : { actualLenderAmount: value };
    const result = await editTitlePremium({ orderId, body });
    if (checkForMutationSuccess(result, `sending title premium update request`)) {
      onUpdatePageClick();
      setPremiumModal(false);
    }
    return { message: '' };
  }, [policyType, orderId, editTitlePremium, onUpdatePageClick]);

  return (
    <Grid columnCount={4}>
      <GridItem columnStart={1} columnEnd={5}>
        <h2>Transaction Details</h2>
      </GridItem>
      <GridItem columnStart={1} columnEnd={4}>
        <FormCell label={'Property address'}>
          <OpenLinkInNewTab link={editAddressUrl} onClick={onOpenNewTabClick}>
            <PropertyAddress address={data.propertyAddress} />
          </OpenLinkInNewTab>
        </FormCell>
      </GridItem>
      <FormCell label={'County'}>
        <OpenLinkInNewTab link={editAddressUrl} onClick={onOpenNewTabClick}>
          {data.propertyAddress === null ? 'None' : data.county}
        </OpenLinkInNewTab>
      </FormCell>
      <FormCell label={'File number'}>{data.fileNumber || 'None'}</FormCell>
      <FormCell label={'Loan number'}>
        <div>
          <ModalTriggerButton fieldName={eSingleFields.loan_number}>
            {data.loanNumber ? data.loanNumber : 'None'}
          </ModalTriggerButton>
        </div>
      </FormCell>
      <FormCell label={'AMT of Insurance'}>
        <div>
          <ModalTriggerButton fieldName={editAmtOfInsuredFieldName}>
            <PriceText price={data.liabilityAmount} />
          </ModalTriggerButton>
        </div>
      </FormCell>
      <FormCell label={'Premium'}>
        <Show
          when={data.transactionCategory === eTransactionCategory.Purchase}
          fallback={
            <OpenLinkInNewTab link={cdBuilderPage} onClick={onOpenNewTabClick}>
              <PremiumPrice premium={data.premiums} />
            </OpenLinkInNewTab>
          }
        >
          <NavigationItem text={''} isInline onClick={() => setPremiumModal(true)}>
            <PremiumPrice premium={data.premiums} />
          </NavigationItem>
        </Show>
      </FormCell>
      <GridItem columnStart={1} columnEnd={3}>
        <FormCell label={'Name of insured'}>
          <OpenLinkInNewTab link={editBorrowerPage} onClick={onOpenNewTabClick}>
            {data.nameOfInsured ? data.nameOfInsured : 'None'}
          </OpenLinkInNewTab>
        </FormCell>
      </GridItem>
      {showMortgageeClause ? (
        <GridItem columnStart={3} columnEnd={5}>
          <FormCell label={'Insured clause'}>
            {data.mortgageeClause ? data.mortgageeClause : 'None'}
          </FormCell>
        </GridItem>
      ) : null}
      <PageWithOutsideLinks
        outsideClick={onOutsideClick}
        showUpdateSpinner={showSaveSpinner}
        showUpdateSuccess={showSaveSuccess}
        pageContainsError={errorMessage}
        onUpdatePageClick={onUpdatePageClickHandler}
      />
      {showPremiumModal ? (
        <EditSingleFieldController<number | null>
          displayLabel={'Premium'}
          inputType={eEditSingleFieldInputType.Currency}
          value={premiumValue}
          validationSchema={yup.number().required()}
          actions={{
            onClose: () => setPremiumModal(false),
            doSubmit: submitPremiumUpdate,
          }}
        />
      ) : null}
    </Grid>
  );
};

export default observer(OrderDetails);
